import { useState, useEffect } from "react"
import {
  Box,
  Flex,
  Text,
  Heading,
  Spinner,
  Select,
  SimpleGrid,
  Table,
  Thead,
  Tbody,
  Tr,
  Th,
  Td,
  Badge,
  useColorModeValue,
  Button,
} from "@chakra-ui/react"
import { DownloadIcon } from "@chakra-ui/icons"
import axios from "axios"
import ExcelJS from "exceljs"

const PERIOD_OPTIONS = [
  { value: "1", label: "Last 1 Month" },
  { value: "3", label: "Last 3 Months" },
  { value: "6", label: "Last 6 Months" },
  { value: "12", label: "Last 12 Months" },
]

const Bottom10 = ({ supplier, retailer }) => {
  const [loading, setLoading] = useState(false)
  const [storesData, setStoresData] = useState([])
  const [articlesData, setArticlesData] = useState([])
  const [periodMonths, setPeriodMonths] = useState("3")
  const [downloading, setDownloading] = useState(false)

  const cardBg = useColorModeValue("white", "gray.800")
  const borderColor = useColorModeValue("gray.200", "gray.700")
  const headerBg = useColorModeValue("gray.50", "gray.700")
  const hoverBg = useColorModeValue("gray.50", "gray.700")

  useEffect(() => {
    if (!supplier || !retailer) return
    const controller = new AbortController()
    fetchData(controller.signal)
    return () => controller.abort()
  }, [supplier, retailer, periodMonths])

  const fetchData = async (signal) => {
    setLoading(true)
    try {
      const months = parseInt(periodMonths)
      const endDate = new Date()
      const startDate = new Date(endDate.getFullYear(), endDate.getMonth() - months, 1)
      const fmt = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`

      const res = await axios.post(
        `${process.env.REACT_APP_API_URL}/insights/bottom10`,
        {
          supplier,
          retailer,
          userid: localStorage.getItem("userid"),
          startDate: fmt(startDate),
          endDate: fmt(endDate),
        },
        { signal }
      )

      if (res.data.status === 1) {
        setStoresData(res.data.data.stores || [])
        setArticlesData(res.data.data.articles || [])
      } else {
        setStoresData([])
        setArticlesData([])
      }
    } catch (err) {
      if (!axios.isCancel(err)) console.error("Bottom10 fetch error:", err)
    } finally {
      setLoading(false)
    }
  }

  const formatCurrency = (val) => {
    const num = parseFloat(val) || 0
    return `R ${num.toLocaleString("en-ZA", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  }

  const handleDownload = async () => {
    setDownloading(true)
    try {
      const workbook = new ExcelJS.Workbook()

      if (storesData.length > 0) {
        const ws1 = workbook.addWorksheet("Bottom 10 Stores")
        ws1.columns = [
          { header: "#", key: "rank", width: 5 },
          { header: "Store Code", key: "site_code", width: 12 },
          { header: "Store Name", key: "site_description", width: 35 },
          { header: "Units", key: "total_units", width: 12 },
          { header: "Amount", key: "total_amount", width: 15 },
        ]
        storesData.forEach((row, i) => {
          ws1.addRow({
            rank: i + 1,
            site_code: row.site_code,
            site_description: row.site_description,
            total_units: parseInt(row.total_units) || 0,
            total_amount: parseFloat(row.total_amount) || 0,
          })
        })
        ws1.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws1.getColumn("total_amount").numFmt = '#,##0.00'
      }

      if (articlesData.length > 0) {
        const ws2 = workbook.addWorksheet("Bottom 10 Articles")
        ws2.columns = [
          { header: "#", key: "rank", width: 5 },
          { header: "Article Code", key: "article_code", width: 15 },
          { header: "Description", key: "articledescription", width: 35 },
          { header: "Brand", key: "brand", width: 18 },
          { header: "Units", key: "total_units", width: 12 },
          { header: "Amount", key: "total_amount", width: 15 },
        ]
        articlesData.forEach((row, i) => {
          ws2.addRow({
            rank: i + 1,
            article_code: row.article_code,
            articledescription: row.articledescription,
            brand: row.brand || "-",
            total_units: parseInt(row.total_units) || 0,
            total_amount: parseFloat(row.total_amount) || 0,
          })
        })
        ws2.getRow(1).eachCell((cell) => {
          cell.font = { bold: true, color: { argb: "FFFFFFFF" } }
          cell.fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF58b6c0" } }
        })
        ws2.getColumn("total_amount").numFmt = '#,##0.00'
      }

      const buffer = await workbook.xlsx.writeBuffer()
      const blob = new Blob([buffer], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" })
      const link = document.createElement("a")
      link.href = window.URL.createObjectURL(blob)
      link.download = `Bottom10_${supplier}_${retailer}_${periodMonths}m.xlsx`
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
    } catch (err) {
      console.error("Download error:", err)
    } finally {
      setDownloading(false)
    }
  }

  const hasData = storesData.length > 0 || articlesData.length > 0

  return (
    <Box>
      {/* Controls */}
      <Flex mb={4} gap={4} align="flex-end" wrap="wrap">
        <Box>
          <Text fontSize="sm" fontWeight="bold" mb={1}>
            Period
          </Text>
          <Select
            value={periodMonths}
            onChange={(e) => setPeriodMonths(e.target.value)}
            size="sm"
            w="160px"
            bg={cardBg}
          >
            {PERIOD_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </Select>
        </Box>
        {hasData && (
          <Button
            size="sm"
            leftIcon={<DownloadIcon />}
            colorScheme="teal"
            onClick={handleDownload}
            isLoading={downloading}
            loadingText="Downloading"
          >
            Download Report
          </Button>
        )}
      </Flex>

      {loading ? (
        <Flex justify="center" py={10}>
          <Spinner size="xl" color="teal.500" />
        </Flex>
      ) : !hasData ? (
        <Box textAlign="center" py={10}>
          <Text color="gray.500">No sales data found for this period</Text>
        </Box>
      ) : (
        <SimpleGrid columns={{ base: 1, lg: 2 }} spacing={4}>
          {/* Bottom 10 Stores */}
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
            <Box p={3} borderBottom="1px" borderColor={borderColor}>
              <Flex justify="space-between" align="center">
                <Heading size="sm">Bottom 10 Stores</Heading>
                <Badge colorScheme="orange">{storesData.length}</Badge>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Stores with the lowest sales value. These may need rep attention, better shelf placement, or promotional support.
              </Text>
            </Box>
            <Box maxH="400px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th w="30px">#</Th>
                    <Th>Store</Th>
                    <Th isNumeric>Units</Th>
                    <Th isNumeric>Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {storesData.map((row, i) => (
                    <Tr key={i} _hover={{ bg: hoverBg }}>
                      <Td fontSize="xs" color="gray.500">
                        {i + 1}
                      </Td>
                      <Td fontSize="xs" maxW="220px" isTruncated>
                        {row.site_description}
                      </Td>
                      <Td isNumeric fontSize="xs">
                        {parseInt(row.total_units || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color="orange.500">
                        {formatCurrency(row.total_amount)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>

          {/* Bottom 10 Articles */}
          <Box bg={cardBg} borderRadius="lg" border="1px" borderColor={borderColor} boxShadow="sm">
            <Box p={3} borderBottom="1px" borderColor={borderColor}>
              <Flex justify="space-between" align="center">
                <Heading size="sm">Bottom 10 Articles</Heading>
                <Badge colorScheme="orange">{articlesData.length}</Badge>
              </Flex>
              <Text fontSize="xs" color="gray.500" mt={1}>
                Lowest-selling articles by value. Consider whether these need promotion, re-ranging, or discontinuation.
              </Text>
            </Box>
            <Box maxH="400px" overflowY="auto">
              <Table size="sm" variant="simple">
                <Thead bg={headerBg} position="sticky" top={0} zIndex={1}>
                  <Tr>
                    <Th w="30px">#</Th>
                    <Th>Article</Th>
                    <Th>Brand</Th>
                    <Th isNumeric>Units</Th>
                    <Th isNumeric>Amount</Th>
                  </Tr>
                </Thead>
                <Tbody>
                  {articlesData.map((row, i) => (
                    <Tr key={i} _hover={{ bg: hoverBg }}>
                      <Td fontSize="xs" color="gray.500">
                        {i + 1}
                      </Td>
                      <Td fontSize="xs" maxW="200px" isTruncated>
                        {row.articledescription}
                      </Td>
                      <Td fontSize="xs">{row.brand || "-"}</Td>
                      <Td isNumeric fontSize="xs">
                        {parseInt(row.total_units || 0).toLocaleString()}
                      </Td>
                      <Td isNumeric fontSize="xs" fontWeight="bold" color="orange.500">
                        {formatCurrency(row.total_amount)}
                      </Td>
                    </Tr>
                  ))}
                </Tbody>
              </Table>
            </Box>
          </Box>
        </SimpleGrid>
      )}
    </Box>
  )
}

export default Bottom10
